/**
 * Diya Glow Effect (Diwali)
 * Soft glowing diyas – very subtle, low opacity. Footer/bottom area only.
 */

import { useEffect, useRef } from 'react'

interface DiyaGlowProps {
  intensity?: 'low' | 'medium'
}

interface Glow {
  x: number
  y: number
  radius: number
  opacity: number
  flicker: number
}

export function DiyaGlow({ intensity = 'low' }: DiyaGlowProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const glowsRef = useRef<Glow[]>([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    const count = intensity === 'low' ? 5 : 10
    const maxRadius = intensity === 'low' ? 40 : 55

    const init = () => {
      glowsRef.current = []
      for (let i = 0; i < count; i++) {
        glowsRef.current.push({
          x: (canvas.width / (count + 1)) * (i + 1) + (Math.random() - 0.5) * 60,
          y: canvas.height - 40 - Math.random() * 80,
          radius: 20 + Math.random() * maxRadius,
          opacity: 0.08 + Math.random() * 0.06,
          flicker: Math.random() * Math.PI * 2,
        })
      }
    }

    init()

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      glowsRef.current.forEach((g) => {
        g.flicker += 0.03
        const o = g.opacity * (0.85 + Math.sin(g.flicker) * 0.15)

        const gradient = ctx.createRadialGradient(g.x, g.y, 0, g.x, g.y, g.radius)
        gradient.addColorStop(0, `rgba(255, 180, 50, ${o})`)
        gradient.addColorStop(0.6, `rgba(255, 200, 100, ${o * 0.4})`)
        gradient.addColorStop(1, 'rgba(255, 180, 50, 0)')

        ctx.beginPath()
        ctx.arc(g.x, g.y, g.radius, 0, Math.PI * 2)
        ctx.fillStyle = gradient
        ctx.fill()
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    }
  }, [intensity])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      aria-hidden="true"
    />
  )
}
